import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import InventoryService from '../services/InventoryService';
import OrderService from '../../orders/services/OrderService';
import { InventoryList, SessionItem, SessionResult } from '../types';
import { useUserRoleContext } from '../../../context/UserRoleContext';

type Phase = 'overview' | 'counting' | 'result';

const COLORS = {
  navy: '#0E1333',
  pink: '#E7007D',
  slate: '#5271A5',
  text: '#334155',
  muted: '#64748b',
  border: '#e0e3ef',
  white: '#ffffff',
  green: '#16a34a',
  amber: '#f59e0b',
  red: '#ef4444',
  lightGray: '#f1f5f9',
  backdrop: 'rgba(0, 0, 0, 0.4)',
};

const GRADIENTS = {
  cta: 'linear-gradient(135deg, #0E1333 0%, #E7007D 100%)',
  progress: 'linear-gradient(90deg, #5271A5, #E7007D)',
  counter: 'linear-gradient(135deg, #0E1333 0%, #5271A5 100%)',
};

const CATEGORY_ICONS: Record<string, string> = {
  ice: '❄️',
  spoon: '🥄',
  drink: '🥤',
  hygiene: '🧴',
  topping: '🍫',
  cone: '🍦',
  packaging: '📦',
  cleaning: '🧹',
};

function getCategoryIcon(iconKey: string): string {
  return CATEGORY_ICONS[iconKey] || '📋';
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function InventoryPage() {
  const { workAreaIds } = useUserRoleContext();
  const activeWorkAreaId = workAreaIds[0] || null;

  const [phase, setPhase] = useState<Phase>('overview');
  const [categories, setCategories] = useState<InventoryList[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [categoryItems, setCategoryItems] = useState<InventoryList | null>(null);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);

  const [sessionId, setSessionId] = useState<number | null>(null);
  const [sessionItems, setSessionItems] = useState<SessionItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentQuantity, setCurrentQuantity] = useState(0);
  const [showNumpad, setShowNumpad] = useState(false);
  const [numpadValue, setNumpadValue] = useState('0');

  const [sessionResult, setSessionResult] = useState<SessionResult | null>(null);
  const [creatingOrders, setCreatingOrders] = useState<Record<string, boolean>>({});
  const [createdOrders, setCreatedOrders] = useState<Record<string, boolean>>({});

  const [countedMap, setCountedMap] = useState<Record<number, number | null>>({});

  const workAreaName = sessionResult?.work_area || 'Work Area';

  useEffect(() => {
    loadCategories();
  }, []);

  async function loadCategories() {
    setLoadingCategories(true);
    try {
      const lists = await InventoryService.getLists();
      setCategories(lists);
      if (lists.length > 0 && !selectedCategoryId) {
        setSelectedCategoryId(lists[0].id);
      }
    } catch (err) {
      console.error('Failed to load inventory categories:', err);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  }

  useEffect(() => {
    if (selectedCategoryId !== null) {
      loadCategoryDetail(selectedCategoryId);
    }
  }, [selectedCategoryId]);

  async function loadCategoryDetail(listId: number) {
    setLoadingItems(true);
    try {
      const detail = await InventoryService.getListDetail(listId);
      setCategoryItems(detail);
    } catch (err) {
      console.error('Failed to load category detail:', err);
      setCategoryItems(null);
    } finally {
      setLoadingItems(false);
    }
  }

  const totalItemCount = useMemo(
    () => categories.reduce((sum, cat) => sum + cat.item_count, 0),
    [categories]
  );

  const totalCountedInSession = useMemo(
    () => Object.values(countedMap).filter((v) => v !== null && v !== undefined).length,
    [countedMap]
  );

  const categoryCountedMap = useMemo(() => {
    const map: Record<string, number> = {};
    for (const item of sessionItems) {
      const key = item.category;
      if (!map[key]) map[key] = 0;
      if (item.counted || countedMap[item.id] !== undefined) {
        map[key]++;
      }
    }
    return map;
  }, [sessionItems, countedMap]);

  const categoryCounts = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const item of sessionItems) {
      const key = item.category;
      totals[key] = (totals[key] || 0) + 1;
    }
    return totals;
  }, [sessionItems]);

  async function handleStartCounting() {
    if (!activeWorkAreaId) return;
    try {
      const session = await InventoryService.startSession(activeWorkAreaId);
      setSessionId(session.session_id);
      const items = await InventoryService.getSessionItems(session.session_id);
      setSessionItems(items);
      setCurrentIndex(0);
      setCurrentQuantity(0);
      setCountedMap({});
      setPhase('counting');
    } catch (err) {
      console.error('Failed to start counting session:', err);
    }
  }

  const currentItem = sessionItems[currentIndex] || null;
  const isLastItem = currentIndex === sessionItems.length - 1;
  const progressPercent =
    sessionItems.length > 0
      ? Math.round(((currentIndex + 1) / sessionItems.length) * 100)
      : 0;

  const handleCounterTap = useCallback(() => {
    setCurrentQuantity((prev) => prev + 1);
  }, []);

  const handleCounterDecrement = useCallback(() => {
    setCurrentQuantity((prev) => Math.max(0, prev - 1));
  }, []);

  async function handleNextItem() {
    if (!sessionId || !currentItem) return;

    try {
      await InventoryService.countItem(sessionId, currentItem.id, currentQuantity);
      setCountedMap((prev) => ({ ...prev, [currentItem.id]: currentQuantity }));

      if (isLastItem) {
        await InventoryService.completeSession(sessionId);
        const result = await InventoryService.getSessionResult(sessionId);
        setSessionResult(result);
        setPhase('result');
      } else {
        setCurrentIndex((prev) => prev + 1);
        setCurrentQuantity(0);
      }
    } catch (err) {
      console.error('Failed to count item:', err);
    }
  }

  async function handleSkipItem() {
    if (!sessionId || !currentItem) return;

    try {
      await InventoryService.skipItem(sessionId, currentItem.id);

      if (isLastItem) {
        await InventoryService.completeSession(sessionId);
        const result = await InventoryService.getSessionResult(sessionId);
        setSessionResult(result);
        setPhase('result');
      } else {
        setCurrentIndex((prev) => prev + 1);
        setCurrentQuantity(0);
      }
    } catch (err) {
      console.error('Failed to skip item:', err);
    }
  }

  function handleNumpadKey(key: string) {
    if (key === 'C') {
      setNumpadValue('0');
    } else if (key === '✓') {
      setCurrentQuantity(parseInt(numpadValue, 10) || 0);
      setShowNumpad(false);
      setNumpadValue('0');
    } else {
      setNumpadValue((prev) => {
        const newVal = prev === '0' ? key : prev + key;
        return newVal.length <= 5 ? newVal : prev;
      });
    }
  }

  const [ordersGenerated, setOrdersGenerated] = useState(false);

  async function handleCreateOrders(supplierName: string) {
    if (!sessionId || !activeWorkAreaId || ordersGenerated) return;
    const allSuppliers = sessionResult?.order_suggestions?.map(s => s.supplier) || [supplierName];
    const markAll: Record<string, boolean> = {};
    allSuppliers.forEach(s => { markAll[s] = true; });
    setCreatingOrders(markAll);
    try {
      await OrderService.generateFromSession(sessionId, activeWorkAreaId);
      setOrdersGenerated(true);
      setCreatedOrders(markAll);
    } catch (err) {
      console.error('Failed to create orders:', err);
    } finally {
      setCreatingOrders({});
    }
  }

  function handleBackToOverview() {
    setPhase('overview');
    setSessionId(null);
    setSessionItems([]);
    setSessionResult(null);
    setCurrentIndex(0);
    setCurrentQuantity(0);
    setCountedMap({});
    setCreatedOrders({});
    setOrdersGenerated(false);
    loadCategories();
  }

  function getCategoryCountedForOverview(category: InventoryList): number {
    if (sessionItems.length === 0) return 0;
    return categoryCountedMap[category.name] || 0;
  }

  function getCategoryTotalForOverview(category: InventoryList): number {
    if (sessionItems.length > 0) {
      return categoryCounts[category.name] || category.item_count;
    }
    return category.item_count;
  }

  const cardStyle: React.CSSProperties = {
    background: COLORS.white,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
  };

  if (loadingCategories && phase === 'overview') {
    return (
      <div style={{ padding: 32, color: COLORS.muted, fontSize: 15 }}>
        Loading inventory...
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 28px 16px',
          borderBottom: `1px solid ${COLORS.border}`,
          flexShrink: 0,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: COLORS.navy }}>
            Inventory Check
          </h1>
          <span style={{ fontSize: 13, color: COLORS.muted }}>{workAreaName}</span>
        </div>
        {phase === 'overview' && (
          <div
            style={{
              fontSize: 14,
              color: COLORS.slate,
              fontWeight: 600,
              background: COLORS.lightGray,
              padding: '6px 14px',
              borderRadius: 20,
            }}
          >
            {totalCountedInSession} / {totalItemCount} counted
          </div>
        )}
        {phase === 'result' && sessionResult && (
          <div style={{ fontSize: 13, color: COLORS.muted }}>
            Completed {formatDate(sessionResult.completed_at)}
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        {phase === 'overview' && renderOverview()}
        {phase === 'counting' && renderCounting()}
        {phase === 'result' && renderResult()}
      </div>

      <AnimatePresence>
        {showNumpad && renderNumpadOverlay()}
      </AnimatePresence>
    </div>
  );

  function renderOverview() {
    return (
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <div
          style={{
            width: '35%',
            minWidth: 260,
            maxWidth: 360,
            borderRight: `1px solid ${COLORS.border}`,
            padding: '20px 16px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
            {categories.map((category) => {
              const isSelected = selectedCategoryId === category.id;
              const counted = getCategoryCountedForOverview(category);
              const total = getCategoryTotalForOverview(category);
              const progressFraction = total > 0 ? counted / total : 0;

              return (
                <div
                  key={category.id}
                  onClick={() => setSelectedCategoryId(category.id)}
                  style={{
                    ...cardStyle,
                    padding: '14px 16px',
                    cursor: 'pointer',
                    border: isSelected
                      ? `2px dashed ${COLORS.pink}`
                      : `1px solid ${COLORS.border}`,
                    transition: 'border 0.15s ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background: COLORS.navy,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 22,
                        flexShrink: 0,
                      }}
                    >
                      {getCategoryIcon(category.icon)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 600,
                          color: COLORS.text,
                          marginBottom: 2,
                        }}
                      >
                        {category.name}
                      </div>
                      <div style={{ fontSize: 12, color: COLORS.muted }}>
                        {counted} / {total} counted
                      </div>
                      <div
                        style={{
                          marginTop: 8,
                          height: 4,
                          borderRadius: 2,
                          background: COLORS.border,
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${progressFraction * 100}%`,
                            height: '100%',
                            borderRadius: 2,
                            background: GRADIENTS.progress,
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <button
            onClick={handleStartCounting}
            style={{
              marginTop: 20,
              width: '100%',
              height: 56,
              background: GRADIENTS.cta,
              color: COLORS.white,
              border: 'none',
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              letterSpacing: 0.3,
              flexShrink: 0,
            }}
          >
            Start Counting
          </button>
        </div>

        <div
          style={{
            flex: 1,
            padding: '20px 24px',
            overflow: 'auto',
          }}
        >
          {loadingItems ? (
            <div style={{ color: COLORS.muted, fontSize: 14, padding: 20 }}>Loading items...</div>
          ) : categoryItems ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 16,
                }}
              >
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: COLORS.navy }}>
                  {categoryItems.name}
                </h2>
                <span style={{ fontSize: 13, color: COLORS.muted }}>
                  {categoryItems.items?.length || 0} items
                </span>
              </div>
              <div style={{ ...cardStyle, overflow: 'hidden' }}>
                {(categoryItems.items || []).map((item, idx) => {
                  const countedValue = countedMap[item.id];
                  const hasCounted = countedValue !== undefined && countedValue !== null;
                  const isBelowMin = hasCounted && countedValue < item.min_stock;
                  const isAboveMin = hasCounted && countedValue >= item.min_stock;

                  return (
                    <div
                      key={item.id}
                      style={{
                        padding: '14px 18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom:
                          idx < (categoryItems.items?.length || 0) - 1
                            ? `1px solid ${COLORS.border}`
                            : 'none',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.text }}>
                          {item.name}
                        </div>
                        <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 2 }}>
                          {item.pack_size} &middot; Min: {item.min_stock}
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: 18,
                          fontWeight: 700,
                          color: isBelowMin
                            ? COLORS.red
                            : isAboveMin
                            ? COLORS.green
                            : COLORS.muted,
                          minWidth: 36,
                          textAlign: 'right',
                        }}
                      >
                        {hasCounted ? countedValue : '—'}
                      </div>
                    </div>
                  );
                })}
                {(!categoryItems.items || categoryItems.items.length === 0) && (
                  <div style={{ padding: 24, textAlign: 'center', color: COLORS.muted, fontSize: 14 }}>
                    No items in this category
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={{ color: COLORS.muted, fontSize: 14, padding: 20 }}>
              Select a category to view items
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderCounting() {
    return (
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <div
          style={{
            width: '35%',
            minWidth: 260,
            maxWidth: 360,
            borderRight: `1px solid ${COLORS.border}`,
            padding: '20px 16px',
            overflow: 'auto',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {categories.map((category) => {
              const counted = categoryCountedMap[category.name] || 0;
              const total = categoryCounts[category.name] || category.item_count;
              const progressFraction = total > 0 ? counted / total : 0;
              const isCurrentCategory = currentItem?.category === category.name;

              return (
                <div
                  key={category.id}
                  style={{
                    ...cardStyle,
                    padding: '14px 16px',
                    border: isCurrentCategory
                      ? `2px dashed ${COLORS.pink}`
                      : `1px solid ${COLORS.border}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background: COLORS.navy,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 22,
                        flexShrink: 0,
                      }}
                    >
                      {getCategoryIcon(category.icon)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 600,
                          color: COLORS.text,
                          marginBottom: 2,
                        }}
                      >
                        {category.name}
                      </div>
                      <div style={{ fontSize: 12, color: COLORS.muted }}>
                        {counted} / {total} counted
                      </div>
                      <div
                        style={{
                          marginTop: 8,
                          height: 4,
                          borderRadius: 2,
                          background: COLORS.border,
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${progressFraction * 100}%`,
                            height: '100%',
                            borderRadius: 2,
                            background: GRADIENTS.progress,
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: '24px 32px',
            overflow: 'auto',
          }}
        >
          <div style={{ marginBottom: 28 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 8,
                fontSize: 13,
                color: COLORS.muted,
              }}
            >
              <span>
                Item {currentIndex + 1} of {sessionItems.length}
              </span>
              <span style={{ fontWeight: 600, color: COLORS.slate }}>{progressPercent}%</span>
            </div>
            <div
              style={{
                height: 4,
                borderRadius: 2,
                background: COLORS.border,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${progressPercent}%`,
                  height: '100%',
                  borderRadius: 2,
                  background: GRADIENTS.progress,
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>

          {currentItem && (
            <AnimatePresence mode="wait">
              <motion.div
                key={currentItem.id}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.2 }}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 700,
                      color: COLORS.navy,
                      marginBottom: 6,
                    }}
                  >
                    {currentItem.name}
                  </div>
                  <div style={{ fontSize: 14, color: COLORS.muted }}>
                    {currentItem.pack_size}
                  </div>
                  <div style={{ fontSize: 13, color: COLORS.muted, marginTop: 2 }}>
                    Min: {currentItem.min_stock}
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 24,
                    marginBottom: 20,
                  }}
                >
                  <button
                    onClick={handleCounterDecrement}
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      background: COLORS.lightGray,
                      border: `1px solid ${COLORS.border}`,
                      fontSize: 22,
                      fontWeight: 600,
                      color: COLORS.text,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                    }}
                  >
                    −
                  </button>

                  <motion.div
                    onClick={handleCounterTap}
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                    style={{
                      width: 176,
                      height: 176,
                      borderRadius: '50%',
                      background: GRADIENTS.counter,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      userSelect: 'none',
                      boxShadow:
                        currentQuantity >= currentItem.min_stock
                          ? `0 0 0 4px ${COLORS.white}, 0 0 0 7px ${COLORS.slate}`
                          : '0 4px 20px rgba(14, 19, 51, 0.25)',
                      transition: 'box-shadow 0.3s ease',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 48,
                        fontWeight: 700,
                        color: COLORS.white,
                        lineHeight: 1,
                      }}
                    >
                      {currentQuantity}
                    </span>
                  </motion.div>

                  <button
                    onClick={() => {
                      setNumpadValue(String(currentQuantity));
                      setShowNumpad(true);
                    }}
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      background: COLORS.lightGray,
                      border: `1px solid ${COLORS.border}`,
                      fontSize: 14,
                      fontWeight: 700,
                      color: COLORS.text,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    123
                  </button>
                </div>

                <div style={{ textAlign: 'center', marginBottom: 32, minHeight: 24 }}>
                  {currentQuantity > 0 && currentQuantity < currentItem.min_stock && (
                    <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.amber }}>
                      Below minimum ({currentItem.min_stock})
                    </span>
                  )}
                  {currentQuantity >= currentItem.min_stock && currentQuantity > 0 && (
                    <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.green }}>
                      Above minimum ✓
                    </span>
                  )}
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    maxWidth: 400,
                  }}
                >
                  <button
                    onClick={handleSkipItem}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: COLORS.muted,
                      fontSize: 15,
                      fontWeight: 500,
                      cursor: 'pointer',
                      padding: '10px 20px',
                    }}
                  >
                    Skip
                  </button>
                  <button
                    onClick={handleNextItem}
                    style={{
                      background: GRADIENTS.cta,
                      color: COLORS.white,
                      border: 'none',
                      borderRadius: 10,
                      padding: '14px 36px',
                      fontSize: 15,
                      fontWeight: 700,
                      cursor: 'pointer',
                      letterSpacing: 0.3,
                    }}
                  >
                    {isLastItem ? 'Finish →' : 'Next →'}
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
    );
  }

  function renderResult() {
    if (!sessionResult) return null;

    const groupedByCategory: Record<string, typeof sessionResult.counted_items> = {};
    for (const item of sessionResult.counted_items) {
      const key = item.supplier;
      if (!groupedByCategory[key]) groupedByCategory[key] = [];
      groupedByCategory[key].push(item);
    }

    const categoryGrouped: Record<string, typeof sessionResult.counted_items> = {};
    for (const item of sessionResult.counted_items) {
      const matchingSession = sessionItems.find((si) => si.id === item.item_id);
      const categoryName = matchingSession?.category || 'Other';
      if (!categoryGrouped[categoryName]) categoryGrouped[categoryName] = [];
      categoryGrouped[categoryName].push(item);
    }

    return (
      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
        <div
          style={{
            ...cardStyle,
            padding: '18px 22px',
            marginBottom: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.navy }}>
              Session #{sessionResult.session_id}
            </div>
            <div style={{ fontSize: 13, color: COLORS.muted, marginTop: 2 }}>
              {sessionResult.work_area} &middot; {sessionResult.counted_by}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 13, color: COLORS.muted }}>
              {formatDate(sessionResult.started_at)} — {formatDate(sessionResult.completed_at)}
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: COLORS.green,
                marginTop: 2,
              }}
            >
              {sessionResult.counted_items.length} items counted
            </div>
          </div>
        </div>

        <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.navy, marginBottom: 14 }}>
          Counted Items
        </h3>

        {Object.entries(categoryGrouped).map(([categoryName, items]) => (
          <div key={categoryName} style={{ marginBottom: 20 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: COLORS.slate,
                marginBottom: 8,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              {categoryName}
            </div>
            <div style={{ ...cardStyle, overflow: 'hidden' }}>
              {items.map((item, idx) => (
                <div
                  key={item.item_id}
                  style={{
                    padding: '12px 18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: idx < items.length - 1 ? `1px solid ${COLORS.border}` : 'none',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>
                      {item.name}
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 1 }}>
                      {item.pack_size} &middot; Min: {item.min_stock}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: item.is_below_minimum ? COLORS.red : COLORS.green,
                      }}
                    >
                      {item.quantity}
                    </span>
                    {item.is_below_minimum && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: COLORS.white,
                          background: COLORS.red,
                          padding: '2px 8px',
                          borderRadius: 10,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                        }}
                      >
                        Order
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {sessionResult.order_suggestions.length > 0 && (
          <>
            <h3
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: COLORS.navy,
                marginTop: 32,
                marginBottom: 14,
              }}
            >
              Order Suggestions
            </h3>

            {sessionResult.order_suggestions.map((suggestion) => (
              <div
                key={suggestion.supplier}
                style={{
                  ...cardStyle,
                  border: `1px solid ${COLORS.amber}`,
                  background: '#fffbeb',
                  marginBottom: 16,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    padding: '14px 18px',
                    borderBottom: `1px solid #fde68a`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>
                    {suggestion.supplier}
                  </div>
                  <button
                    onClick={() => handleCreateOrders(suggestion.supplier)}
                    disabled={
                      creatingOrders[suggestion.supplier] || createdOrders[suggestion.supplier]
                    }
                    style={{
                      background: createdOrders[suggestion.supplier]
                        ? COLORS.green
                        : GRADIENTS.cta,
                      color: COLORS.white,
                      border: 'none',
                      borderRadius: 8,
                      padding: '8px 18px',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: createdOrders[suggestion.supplier] ? 'default' : 'pointer',
                      opacity: creatingOrders[suggestion.supplier] ? 0.7 : 1,
                    }}
                  >
                    {createdOrders[suggestion.supplier]
                      ? 'Orders Created ✓'
                      : creatingOrders[suggestion.supplier]
                      ? 'Creating...'
                      : 'Create Orders'}
                  </button>
                </div>
                {suggestion.items.map((orderItem, idx) => (
                  <div
                    key={orderItem.item_id}
                    style={{
                      padding: '10px 18px',
                      fontSize: 13,
                      color: COLORS.text,
                      borderBottom:
                        idx < suggestion.items.length - 1 ? `1px solid #fde68a` : 'none',
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{orderItem.name}:</span>{' '}
                    <span style={{ color: COLORS.red }}>{orderItem.current_stock}</span>
                    {' → '}
                    <span style={{ color: COLORS.green }}>{orderItem.min_stock}</span>
                    <span style={{ color: COLORS.muted, marginLeft: 8 }}>
                      ({orderItem.quantity_needed} needed)
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </>
        )}

        <div style={{ marginTop: 32, marginBottom: 24, textAlign: 'center' }}>
          <button
            onClick={handleBackToOverview}
            style={{
              background: GRADIENTS.cta,
              color: COLORS.white,
              border: 'none',
              borderRadius: 12,
              padding: '16px 48px',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  function renderNumpadOverlay() {
    const numpadKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '✓'];

    return (
      <motion.div
        key="numpad-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={() => setShowNumpad(false)}
        style={{
          position: 'fixed',
          inset: 0,
          background: COLORS.backdrop,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          zIndex: 1000,
        }}
      >
        <motion.div
          initial={{ y: 300 }}
          animate={{ y: 0 }}
          exit={{ y: 300 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: COLORS.white,
            borderRadius: '20px 20px 0 0',
            padding: '24px 20px 32px',
            width: '100%',
            maxWidth: 400,
          }}
        >
          <div
            style={{
              textAlign: 'center',
              fontSize: 36,
              fontWeight: 700,
              color: COLORS.navy,
              marginBottom: 20,
              padding: '12px 0',
              background: COLORS.lightGray,
              borderRadius: 10,
              minHeight: 60,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {numpadValue}
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 10,
            }}
          >
            {numpadKeys.map((key) => {
              const isConfirm = key === '✓';
              const isClear = key === 'C';

              return (
                <button
                  key={key}
                  onClick={() => handleNumpadKey(key)}
                  style={{
                    height: 56,
                    borderRadius: 10,
                    border: 'none',
                    fontSize: isConfirm ? 22 : 20,
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: isConfirm
                      ? COLORS.green
                      : isClear
                      ? COLORS.amber
                      : COLORS.lightGray,
                    color: isConfirm || isClear ? COLORS.white : COLORS.text,
                  }}
                >
                  {key}
                </button>
              );
            })}
          </div>
        </motion.div>
      </motion.div>
    );
  }
}
